/**
 * Per-order quantity caps, one entry per capped SKU.
 *
 * Configured from layout/theme.liquid via window.PURITION_SKU_QTY_LIMITS:
 *   [{ sku, productId, max, message, cartTotal, lines }, ...]
 *
 * The cart is the one choke point every add path funnels through — product
 * forms, build-a-box, Appstle, quick-add, and a hand-typed /cart/add URL — so
 * each cap is enforced by re-reading the cart after any mutation and trimming
 * the capped SKU back down. Lines are matched on SKU, never on product id
 * alone, so other variants of the same product are never affected.
 *
 * A cap applies across the whole order rather than per cart line, so the same
 * SKU split over several lines (one-time + subscription) still totals `max`.
 *
 * This is a client-side guard: it fixes the cart for real customers, but it is
 * not a security control. Anyone running with JS disabled can still push a
 * larger quantity through to checkout. Real enforcement needs a Shopify
 * cart/checkout validation Function.
 */
(function () {
  'use strict';

  // Diagnostic marker: in the browser console, window.__puritionSkuQtyGuard
  // should read 'active'. 'no-config' means this file loaded but the config
  // block in layout/theme.liquid did not render. undefined means this file
  // never loaded at all.
  window.__puritionSkuQtyGuard = 'loading';

  var RAW = window.PURITION_SKU_QTY_LIMITS;
  if (!Array.isArray(RAW)) {
    // The single-object shape this file used before the cap became a list.
    RAW = window.PURITION_SKU_QTY_LIMIT ? [window.PURITION_SKU_QTY_LIMIT] : [];
  }

  var RULES = [];
  RAW.forEach(function (cfg) {
    if (!cfg) return;
    var sku = String(cfg.sku || '').trim().toUpperCase();
    var productId = Number(cfg.productId) || 0;
    var max = Math.floor(Number(cfg.max));
    // A rule with no usable max would have to guess at a cap, and guessing
    // wrong quietly empties part of a real basket. Drop it instead.
    if (!(max > 0) || (!sku && !productId)) return;
    RULES.push({
      sku: sku,
      productId: productId,
      max: max,
      message: String(cfg.message || '').trim(),
      cartTotal: Number(cfg.cartTotal) || 0,
      lines: Array.isArray(cfg.lines) ? cfg.lines : []
    });
  });

  if (!RULES.length) {
    window.__puritionSkuQtyGuard = 'no-config';
    return;
  }

  var MUTATES_CART = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var RELOAD_GUARD = 'purition-sku-qty-reloaded';
  var NOTICE_FLAG = 'purition-sku-qty-notice';
  var NOTICE_CLASS = 'sku-qty-limit-notice';
  var DRAWER_SECTION = 'cart-drawer';
  var HEADER_SECTION = 'header-cart-status';

  // Captured before the patches below, so this script's own cart calls don't
  // re-trigger it.
  var nativeFetch = window.fetch;
  var busy = false;

  function root(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base.replace(/\/$/, '') + '/' + path;
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  // The drawer sheds `hidden` on window load whether or not it is showing;
  // `open` is the attribute that actually tracks visibility.
  function drawerIsOpen() {
    var el = document.getElementById('shtCartDrawer');
    return !!el && el.hasAttribute('open');
  }

  // SKU is authoritative. product_id is only a fallback for the rare case of a
  // line item that reports no SKU at all — matching on product_id first would
  // wrongly cap sibling variants of the same product.
  function matches(rule, item) {
    if (!item) return false;
    var sku = item.sku ? String(item.sku).trim().toUpperCase() : '';
    if (sku) return rule.sku !== '' && sku === rule.sku;
    return rule.productId !== 0 && Number(item.product_id) === rule.productId;
  }

  function getCart() {
    return nativeFetch(root('cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  // Address lines by index, not by variant id: /cart/change.js with an id would
  // be ambiguous when the same variant sits on more than one line.
  function setLineQuantity(line, quantity) {
    return nativeFetch(root('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/change.js ' + res.status);
      return res.json();
    });
  }

  /**
   * Trim every over-cap SKU back to its max. Resolves with the rules that were
   * actually clamped, so the caller knows which messages to surface.
   */
  function enforce() {
    if (busy) return Promise.resolve([]);
    busy = true;

    return getCart()
      .then(function (cart) {
        var items = cart.items || [];
        var steps = [];
        var clamped = [];

        RULES.forEach(function (rule) {
          var targets = [];
          items.forEach(function (item, i) {
            if (matches(rule, item)) targets.push({ line: i + 1, quantity: item.quantity });
          });
          if (!targets.length) return;

          var total = targets.reduce(function (sum, t) { return sum + t.quantity; }, 0);
          if (total <= rule.max) return;

          var excess = total - rule.max;
          // Take the excess off the newest lines first, so whatever the
          // customer had in the basket originally is what survives.
          targets.sort(function (a, b) { return b.line - a.line; });
          targets.forEach(function (t) {
            if (excess <= 0) return;
            var take = Math.min(excess, t.quantity);
            excess -= take;
            steps.push({ line: t.line, quantity: t.quantity - take });
          });
          clamped.push(rule);
        });

        if (!steps.length) return [];

        // Highest line index first across all rules: setting a line to 0
        // removes it and shifts every line below it up, so descending order
        // keeps the indexes still to be written valid.
        steps.sort(function (a, b) { return b.line - a.line; });

        return steps.reduce(function (chain, step) {
          return chain.then(function () { return setLineQuantity(step.line, step.quantity); });
        }, Promise.resolve()).then(function () { return clamped; });
      })
      .catch(function () { return []; })
      .then(function (result) {
        busy = false;
        return result;
      });
  }

  function store(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (e) { /* private mode */ }
  }

  function read(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  // --- Messaging -----------------------------------------------------------

  function noticeText(rule) {
    if (rule.message) return rule.message;
    return 'There is a limit of ' + rule.max + ' per order on one of the items in your basket, ' +
      'so we have adjusted the quantity.';
  }

  function buildNotice(message) {
    var note = document.createElement('div');
    note.className = NOTICE_CLASS;
    note.setAttribute('role', 'status');
    note.style.cssText =
      'margin:0 0 16px;padding:10px 14px;border-left:3px solid #70AE6D;' +
      'background:rgba(43,62,36,0.05);border-radius:6px;font-size:14px;' +
      'line-height:1.4;text-align:left;';
    note.textContent = message;
    return note;
  }

  function queueNotices(rules) {
    if (!rules.length) return;
    var messages = [];
    rules.forEach(function (rule) {
      var text = noticeText(rule);
      if (messages.indexOf(text) === -1) messages.push(text);
    });
    store(NOTICE_FLAG, JSON.stringify(messages));
  }

  /**
   * Render into whichever cart surface the customer can actually see. Returns
   * false when neither is on screen, which keeps the message queued rather
   * than burning it on a closed drawer.
   */
  function showNotices(messages) {
    var form = document.getElementById('cartForm');
    var parent = null;

    if (isCartPage() && form && form.parentNode) {
      parent = form.parentNode;
    } else if (drawerIsOpen()) {
      var drawer = document.getElementById('cartDrawer');
      parent = drawer && drawer.querySelector('.drawer__body');
    }
    if (!parent) return false;

    // A re-render can leave a stale copy behind; only ever show one set.
    Array.prototype.slice.call(parent.querySelectorAll('.' + NOTICE_CLASS))
      .forEach(function (el) { el.parentNode.removeChild(el); });

    // Resolved after that removal, so we never anchor to a node just detached.
    var before = (isCartPage() && form) ? form : parent.firstChild;
    messages.forEach(function (message) {
      parent.insertBefore(buildNotice(message), before);
    });
    return true;
  }

  function flushNotices() {
    var raw = read(NOTICE_FLAG);
    if (!raw) return;

    var messages;
    try {
      messages = JSON.parse(raw);
    } catch (e) {
      messages = null;
    }
    if (!Array.isArray(messages) || !messages.length) {
      store(NOTICE_FLAG, null);
      return;
    }
    if (showNotices(messages)) store(NOTICE_FLAG, null);
  }

  // --- Keeping the visible cart in step ------------------------------------

  function sectionNode(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Our own /cart/change.js calls are deliberately section-free, so nothing
   * re-renders the drawer or the header count for us. Pull them fresh, or the
   * drawer would go on showing the quantity we just trimmed away.
   */
  function refreshSurfaces() {
    var drawer = document.getElementById('cartDrawer');
    var headerStatus = document.getElementById('headerCartStatus');
    var wanted = [];

    if (drawer) wanted.push(DRAWER_SECTION);
    if (headerStatus) wanted.push(HEADER_SECTION);
    if (!wanted.length) return Promise.resolve();

    // The origin keeps this on our own store. A bare pathname is not safe: on a
    // page reached as //other-site.example/x it reads as a protocol-relative URL,
    // and the reply would be written into the drawer below.
    return nativeFetch(window.location.origin + window.location.pathname + '?sections=' + wanted.join(','), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('sections ' + res.status);
        return res.json();
      })
      .then(function (sections) {
        if (drawer && sections[DRAWER_SECTION]) {
          var freshDrawer = sectionNode(sections[DRAWER_SECTION], '.js-cart-drawer-wrapper');
          var currentDrawer = drawer.querySelector('.js-cart-drawer-wrapper');
          if (freshDrawer && currentDrawer) currentDrawer.innerHTML = freshDrawer.innerHTML;
        }
        if (headerStatus && sections[HEADER_SECTION]) {
          var freshCount = sectionNode(sections[HEADER_SECTION], '.shopify-section');
          if (freshCount) headerStatus.innerHTML = freshCount.innerHTML;
        }
      })
      .catch(function () { /* the cart itself is already correct */ });
  }

  function onClamped(rules) {
    if (!rules.length) return;
    queueNotices(rules);

    if (isCartPage()) {
      // Re-render the cart so line prices and totals match the trimmed
      // quantity. Guarded so a change that somehow fails to stick can't
      // reload in a loop.
      if (read(RELOAD_GUARD)) return;
      store(RELOAD_GUARD, '1');
      window.location.reload();
      return;
    }
    refreshSurfaces().then(flushNotices);
  }

  /**
   * Cart page: stop an over-cap quantity being selectable in the first place.
   * Line indexes come from Liquid, rendered from the same cart as the markup
   * around them.
   */
  function clampCartControls() {
    if (!isCartPage()) return;

    RULES.forEach(function (rule) {
      rule.lines.forEach(function (index) {
        var field = document.getElementById('quantity-' + index);
        if (!field) return;

        if (field.tagName === 'SELECT') {
          Array.prototype.slice.call(field.options).forEach(function (opt) {
            if (Number(opt.value) > rule.max) opt.remove();
          });
        } else {
          field.setAttribute('max', String(rule.max));
        }
        if (Number(field.value) > rule.max) field.value = String(rule.max);
      });
    });
  }

  function recheck() {
    enforce().then(onClamped);
  }

  // --- Catch cart mutations made by any script on the page -----------------
  // Full-page form posts to /cart and /cart/add are covered instead by the
  // load-time pass below, which reads the freshly rendered cart from Liquid.

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch.apply(this, arguments);
      if (MUTATES_CART.test(String(url))) {
        result.then(function () { setTimeout(recheck, 0); }, function () {});
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { this.__puritionCartMutation = MUTATES_CART.test(String(url)); } catch (e) {}
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__puritionCartMutation) {
      this.addEventListener('loadend', function () { setTimeout(recheck, 0); });
    }
    return nativeSend.apply(this, arguments);
  };

  // --- Load-time pass ------------------------------------------------------

  var overCapOnLoad = RULES.some(function (rule) { return rule.cartTotal > rule.max; });
  if (overCapOnLoad) {
    recheck();
  } else {
    store(RELOAD_GUARD, null);
  }

  function onReady() {
    flushNotices();
    clampCartControls();

    // The drawer can open long after a clamp — the customer carries on
    // browsing, then taps the cart icon — so flush anything still queued then.
    var drawerEl = document.getElementById('shtCartDrawer');
    if (drawerEl) drawerEl.addEventListener('opened', flushNotices);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Restored from the back/forward cache: the cart may have moved on since.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) recheck();
  });

  window.__puritionSkuQtyGuard = 'active';
})();
